/**
 * Point-label decluttering: show a label only where it identifies its point
 * unambiguously and does not collide with other labels, markers, or map
 * controls. Selected, hovered, and flagged points are always labeled.
 */

export interface LabelCandidate {
  key: string;
  x: number;
  y: number;
  text: string;
  /** Higher wins when labels compete for space. */
  priority?: number;
  /** Always drawn, even when it collides (selection, hover, flags). */
  force?: boolean;
  /** Clearance around this point (rings make it larger); default markerRadius. */
  radius?: number;
}

export interface PlacedLabel {
  key: string;
  text: string;
  /** Text anchor position (SVG text x/y, baseline). */
  x: number;
  y: number;
  anchor: 'start' | 'middle' | 'end';
}

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface DeclutterOptions {
  width: number;
  height: number;
  /** Radius of the marker each label sits beside. */
  markerRadius?: number;
  fontSize?: number;
  /** Average glyph advance used to estimate label width. */
  charWidth?: number;
  /**
   * A point closer than this to another labeled point is ambiguous at the
   * current zoom; its label waits until the user zooms in.
   */
  minSpacing?: number;
  /** Other markers to keep clear of (keys match their candidates). */
  obstacles?: ReadonlyArray<{ key: string; x: number; y: number }>;
  /** Screen areas occupied by controls. */
  reserved?: ReadonlyArray<Box>;
}

const GAP = 3;
const EDGE = 2;

export const overlaps = (a: Box, b: Box): boolean =>
  a.x < b.x + b.width &&
  b.x < a.x + a.width &&
  a.y < b.y + b.height &&
  b.y < a.y + a.height;

const CELL = 48;
/** Area beyond the pane edges whose points can still affect labels. */
const MARGIN = 48;

/**
 * Uniform-grid spatial hash so collision and spacing checks stay close to
 * linear; labels are re-laid out on every pan frame.
 */
class BoxGrid<T extends Box> {
  private readonly cells = new Map<number, T[]>();

  constructor(private readonly cell: number) {}

  private cellsOf(box: Box, visit: (key: number) => void) {
    const x0 = Math.floor(box.x / this.cell);
    const x1 = Math.floor((box.x + box.width) / this.cell);
    const y0 = Math.floor(box.y / this.cell);
    const y1 = Math.floor((box.y + box.height) / this.cell);
    for (let x = x0; x <= x1; x++) {
      for (let y = y0; y <= y1; y++) visit(x * 100_003 + y);
    }
  }

  add(item: T) {
    this.cellsOf(item, (key) => {
      const bucket = this.cells.get(key);
      if (bucket) bucket.push(item);
      else this.cells.set(key, [item]);
    });
  }

  /** Whether any stored item overlapping `box` passes `accept`. */
  some(box: Box, accept: (item: T) => boolean): boolean {
    let found = false;
    this.cellsOf(box, (key) => {
      if (found) return;
      found = !!this.cells.get(key)?.some((item) => accept(item));
    });
    return found;
  }
}

/** Whether each point has another point closer than `spacing`. */
const crowdedPoints = (
  points: ReadonlyArray<{ x: number; y: number }>,
  spacing: number
): boolean[] => {
  if (spacing <= 0) return points.map(() => false);
  const grid = new BoxGrid<Box & { index: number }>(spacing);
  points.forEach((p, index) =>
    grid.add({ x: p.x, y: p.y, width: 0, height: 0, index })
  );
  return points.map((p, index) =>
    grid.some(
      {
        x: p.x - spacing,
        y: p.y - spacing,
        width: spacing * 2,
        height: spacing * 2,
      },
      (other) =>
        other.index !== index &&
        Math.hypot(other.x - p.x, other.y - p.y) < spacing
    )
  );
};

export const declutterLabels = (
  candidates: ReadonlyArray<LabelCandidate>,
  {
    width,
    height,
    markerRadius = 6,
    fontSize = 11,
    charWidth = 6.6,
    minSpacing = 14,
    obstacles = [],
    reserved = [],
  }: DeclutterOptions
): PlacedLabel[] => {
  const nearPane = (p: { x: number; y: number }) =>
    p.x >= -MARGIN &&
    p.y >= -MARGIN &&
    p.x <= width + MARGIN &&
    p.y <= height + MARGIN;
  const visible = candidates.filter(nearPane);
  if (visible.length === 0) return [];
  const crowded = crowdedPoints(visible, minSpacing);
  const order = visible
    .map((candidate, index) => ({ candidate, index }))
    .filter(({ candidate, index }) => candidate.force || !crowded[index])
    .sort(
      (a, b) =>
        Number(b.candidate.force ?? false) -
          Number(a.candidate.force ?? false) ||
        (b.candidate.priority ?? 0) - (a.candidate.priority ?? 0) ||
        a.index - b.index
    );

  const markers = new BoxGrid<Box & { key: string }>(CELL);
  for (const o of obstacles) {
    if (!nearPane(o)) continue;
    markers.add({
      key: o.key,
      x: o.x - markerRadius,
      y: o.y - markerRadius,
      width: markerRadius * 2,
      height: markerRadius * 2,
    });
  }
  const placed: PlacedLabel[] = [];
  const placedBoxes = new BoxGrid<Box>(CELL);
  const lineHeight = fontSize + 2;

  for (const { candidate } of order) {
    const offset = (candidate.radius ?? markerRadius) + GAP;
    const textWidth = candidate.text.length * charWidth + 2;
    const positions: Array<{ label: PlacedLabel; box: Box }> = [
      // right, left, above, below
      {
        label: {
          key: candidate.key,
          text: candidate.text,
          x: candidate.x + offset,
          y: candidate.y + fontSize * 0.36,
          anchor: 'start',
        },
        box: {
          x: candidate.x + offset - 1,
          y: candidate.y - lineHeight / 2,
          width: textWidth,
          height: lineHeight,
        },
      },
      {
        label: {
          key: candidate.key,
          text: candidate.text,
          x: candidate.x - offset,
          y: candidate.y + fontSize * 0.36,
          anchor: 'end',
        },
        box: {
          x: candidate.x - offset - textWidth + 1,
          y: candidate.y - lineHeight / 2,
          width: textWidth,
          height: lineHeight,
        },
      },
      {
        label: {
          key: candidate.key,
          text: candidate.text,
          x: candidate.x,
          y: candidate.y - offset - 2,
          anchor: 'middle',
        },
        box: {
          x: candidate.x - textWidth / 2,
          y: candidate.y - offset - lineHeight,
          width: textWidth,
          height: lineHeight,
        },
      },
      {
        label: {
          key: candidate.key,
          text: candidate.text,
          x: candidate.x,
          y: candidate.y + offset + fontSize - 1,
          anchor: 'middle',
        },
        box: {
          x: candidate.x - textWidth / 2,
          y: candidate.y + offset,
          width: textWidth,
          height: lineHeight,
        },
      },
    ];
    const inside = (box: Box) =>
      box.x >= EDGE &&
      box.y >= EDGE &&
      box.x + box.width <= width - EDGE &&
      box.y + box.height <= height - EDGE;
    const free = (box: Box) =>
      inside(box) &&
      !reserved.some((area) => overlaps(box, area)) &&
      !placedBoxes.some(box, (other) => overlaps(box, other)) &&
      !markers.some(
        box,
        (marker) => marker.key !== candidate.key && overlaps(box, marker)
      );
    const choice =
      positions.find((position) => free(position.box)) ??
      (candidate.force
        ? (positions.find((position) => inside(position.box)) ?? positions[0])
        : undefined);
    if (!choice) continue;
    placed.push(choice.label);
    placedBoxes.add(choice.box);
  }
  return placed;
};
